import React from 'react';
import { AdminPageHeader } from './components/AdminPageHeader';

// ============================================================================
// Types
// ============================================================================

export interface FeedbackDetail {
  id: number;
  rating: number;
  comments: string | null;
  createdAt: string;
  givenAt: string | null;
  updatedAt: string;
  user: {
    id: number;
    email: string;
    language: string | null;
    createdAt: string;
  };
}

export interface AdminFeedbackDetailPageProps {
  feedback: FeedbackDetail;

  // URLs
  feedbacksPath: string;
  userPath: string;

  // Labels
  labels: {
    title: string;
    backLabel: string;
    submittedAgo: string;
    // Rating
    ratingTitle: string;
    outOf5: string;
    // User
    userInfoTitle: string;
    emailLabel: string;
    userIdLabel: string;
    localeLabel: string;
    memberSinceLabel: string;
    viewUserProfileButton: string;
    agoLabel: string;
    // Comments
    commentsTitle: string;
    noCommentsProvided: string;
    // Timeline
    timelineTitle: string;
    feedbackSubmitted: string;
    ratingGiven: string;
    lastUpdated: string;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================================
// Helper Components
// ============================================================================

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="my-3">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`bi ${star <= rating ? 'bi-star-fill text-warning' : 'bi-star text-muted'}`}
          style={{ fontSize: '2rem' }}
        />
      ))}
    </div>
  );
}

function TimelineItem({
  icon,
  bgColor,
  title,
  datetime,
}: {
  icon: string;
  bgColor: string;
  title: string;
  datetime: string;
}) {
  return (
    <div className="d-flex mb-3">
      <div className="me-3">
        <div
          className={`${bgColor} text-white rounded-circle d-flex align-items-center justify-content-center`}
          style={{ width: 40, height: 40 }}
        >
          <i className={`bi bi-${icon}`} />
        </div>
      </div>
      <div>
        <strong>{title}</strong>
        <br />
        <small className="text-muted">{formatDateTime(datetime)}</small>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminFeedbackDetailPage({
  feedback,
  feedbacksPath,
  userPath,
  labels,
}: AdminFeedbackDetailPageProps) {
  const createdAt = new Date(feedback.createdAt);
  const givenAt = feedback.givenAt ? new Date(feedback.givenAt) : null;
  const updatedAt = new Date(feedback.updatedAt);

  const showGivenAt = givenAt && givenAt.getTime() !== createdAt.getTime();
  const showUpdated = updatedAt.getTime() > createdAt.getTime() + 60000; // More than 1 minute difference

  return (
    <div>
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="h2">
              <i className="bi bi-chat-heart me-2" />
              {labels.title}
            </h1>
            <p className="text-muted mb-0">
              {labels.submittedAgo.replace('{time}', formatTimeAgo(feedback.createdAt))}
            </p>
          </div>
          <a href={feedbacksPath} className="btn btn-outline-secondary">
            <i className="bi bi-arrow-left" /> {labels.backLabel}
          </a>
        </div>
      </div>

      <div className="row g-4">
        {/* Rating Card */}
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body text-center">
              <h5 className="card-title">{labels.ratingTitle}</h5>
              <StarRating rating={feedback.rating} />
              <h2 className="text-warning mb-0">
                {feedback.rating} {labels.outOf5}
              </h2>
            </div>
          </div>
        </div>

        {/* User Information Card */}
        <div className="col-md-8">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-person me-2" />
                {labels.userInfoTitle}
              </h5>
              <div className="row">
                <div className="col-sm-4 mb-2">
                  <strong>{labels.emailLabel}:</strong>
                </div>
                <div className="col-sm-8 mb-2">
                  <a href={userPath} className="text-decoration-none">
                    {feedback.user.email}
                  </a>
                </div>

                <div className="col-sm-4 mb-2">
                  <strong>{labels.userIdLabel}:</strong>
                </div>
                <div className="col-sm-8 mb-2">{feedback.user.id}</div>

                <div className="col-sm-4 mb-2">
                  <strong>{labels.localeLabel}:</strong>
                </div>
                <div className="col-sm-8 mb-2">{feedback.user.language || 'en'}</div>

                <div className="col-sm-4 mb-2">
                  <strong>{labels.memberSinceLabel}:</strong>
                </div>
                <div className="col-sm-8 mb-2">
                  {formatDate(feedback.user.createdAt)}
                  <small className="text-muted">
                    {' '}
                    ({formatTimeAgo(feedback.user.createdAt)} {labels.agoLabel})
                  </small>
                </div>
              </div>

              <div className="mt-3">
                <a href={userPath} className="btn btn-primary">
                  <i className="bi bi-person-circle" /> {labels.viewUserProfileButton}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments Card */}
      <div className="card shadow-sm mt-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            <i className="bi bi-chat-text me-2" />
            {labels.commentsTitle}
          </h5>
          {feedback.comments ? (
            <div className="bg-light p-3 rounded">
              <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {feedback.comments}
              </p>
            </div>
          ) : (
            <div className="text-center text-muted py-4">
              <i className="bi bi-chat" style={{ fontSize: '2rem' }} />
              <p className="mt-2 mb-0">{labels.noCommentsProvided}</p>
            </div>
          )}
        </div>
      </div>

      {/* Timeline Card */}
      <div className="card shadow-sm mt-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            <i className="bi bi-clock-history me-2" />
            {labels.timelineTitle}
          </h5>
          <div className="timeline">
            <TimelineItem
              icon="star-fill"
              bgColor="bg-primary"
              title={labels.feedbackSubmitted}
              datetime={feedback.createdAt}
            />

            {showGivenAt && feedback.givenAt && (
              <TimelineItem
                icon="check-circle"
                bgColor="bg-success"
                title={labels.ratingGiven}
                datetime={feedback.givenAt}
              />
            )}

            {showUpdated && (
              <TimelineItem
                icon="pencil"
                bgColor="bg-info"
                title={labels.lastUpdated}
                datetime={feedback.updatedAt}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminFeedbackDetailPage;
